import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import FloorPlanService from '../../services/floorPlanService';
import { promptToaster } from '../App/actions';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import {
  calculateRentWithAmenities,
  calculateBaseMarketRent,
  getIsAffordableMixedProperty,
  getIsProperty236,
} from '../../utils/lease-helpers';
import {
  getFloorPlanType,
  getAffordablePlanDetails,
  getFloorPlanProgramNames,
  getFloorPlanProgramsCellValue,
  getStoredColumnVisibilityState,
  parse236Rents,
} from './utils';
import { formatFloorPlanBedsBaths } from '../../utils/floorPlan';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import { HEADERS, TOTAL_COLUMNS, PROPERTY_PATH_MAP } from './table-constants';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import HUD_SUBSIDY_CODES from '../../constants/hudSubsidyCodes';
import useUniqueTableName from '../../hooks/useUniqueTableName';

export const useManageFloorplans = ({
  NAME,
  intl,
  selectedProperty,
  openUnitsByFloorplanModal,
  flags,
}) => {
  const name = useUniqueTableName(NAME);
  const { section236Flag } = flags;
  const propertyClass = selectedProperty?.propertyClass?.propertyType;

  const isAffordable = propertyClass === 'Affordable';
  const isCommercialMixed = propertyClass === 'Commercial-Mixed';
  const isMixed = propertyClass === 'Mixed' || isCommercialMixed;
  const isAffordableMixedProperty = getIsAffordableMixedProperty({
    property: selectedProperty,
  });
  const { noteRent, basicRent } = selectedProperty?.setup?.rdProjectType
    ?.rdrents || { noteRent: false, basicRent: false };

  const isPropertySection236 = getIsProperty236({
    property: selectedProperty,
    HUD_SUBSIDY_CODES,
  });
  const { floorPlans, isLoading, refetch } = useFetchManageFloorplans({
    selectedProperty,
    isAffordable,
    isMixed,
  });

  const { updatedHudGrcLogic } = useFlags();

  const { hasHUD, hasLIHTC, hasRD } = getAffordablePlanDetails(
    floorPlans || [],
  );
  const { results } = useParseResults({
    floorPlans,
    selectedProperty,
    isPropertySection236,
    showRDNoteRent: noteRent,
    showRDBasicRent: basicRent,
  });

  const showSection236 =
    section236Flag &&
    (results?.some((fp) => fp.isSection236) || isPropertySection236);

  const filterOptions = useGetFilterOptions({ results });
  const headers = useHeaders({
    isAffordableMixedProperty,
    hasHUD,
    hasLIHTC,
    filterOptions,
    showSection236,
    showRDNoteRent: noteRent,
    showRDBasicRent: basicRent,
  });

  const {
    filterState,
    filterTypeState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    searchState,
    dateState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
  });
  const propertyPathMap = useMemo(
    () =>
      PROPERTY_PATH_MAP({
        showSection236,
        showRDNoteRent: noteRent,
        showRDBasicRent: basicRent,
      }),
    [showSection236, noteRent, basicRent],
  );

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    PROPERTY_PATH_MAP: propertyPathMap,
  });
  const footerRow = useGetFooterRow({
    sortedAndFilteredResults,
    filteredHeaders: headers,
  });

  const rows = useRows({
    intl,
    floorPlans: sortedAndFilteredResults,
    selectedProperty,
    isAffordable,
    isMixed,
    hasHUD,
    hasLIHTC,
    openUnitsByFloorplanModal,
    showSection236,
    showRDNoteRent: noteRent,
    showRDBasicRent: basicRent,
  });

  // A column added to HEADERS is absent from any previously saved column-visibility state, which
  // makes it render as hidden for every user who has already opened this screen. Force a one-time
  // overwrite when the saved state predates a current header.
  const { initialSelectedColumns, shouldForceOverwrite } = useMemo(
    () => getStoredColumnVisibilityState({ headers, name }),
    [headers, name],
  );

  const {
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    handleColumnChange,
    filteredHeaders,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialSelectedColumns,
    forceOverwrite: shouldForceOverwrite,
  });

  const { onCSVButtonClick } = useCSVPDFExport({
    propertyName: selectedProperty?.name,
    hasAnyFilters:
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length,
    filteredHeaders,
    rows,
    results: sortedAndFilteredResults,
  });

  return {
    allColumnsHidden,
    count: rows?.length ?? 0,
    totalCount: results?.length ?? 0,
    floorPlans: rows,
    filteredHeaders,
    columnOptions,
    footerRow,
    isLoading,
    hasHUD,
    hasRD,
    updatedHudGrcLogic,
    isAffordableMixedProperty,
    filterState,
    filterTypeState,
    name,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleColumnChange,
    refetchFloorPlans: refetch,
    selectedColumns,
    searchState,
    onCSVButtonClick,
  };
};

const useFetchManageFloorplans = ({ selectedProperty = {} }) => {
  const { id, organizationId } = selectedProperty;
  const abortController = new AbortController();
  const queryKey = [id, organizationId];
  const options = { signal: abortController.signal };
  const { data, isLoading, isSuccess, refetch } = useQuery(
    ['manageFloorplans', queryKey],
    () => new FloorPlanService().getAll(organizationId, id, options),
    {
      enabled: Boolean(id.length && organizationId.length),
      refetchOnWindowFocus: false,
      onError: (e) => {
        promptToaster({
          type: 'error',
          title: 'Error loading floor plans',
          message: e.toString(),
        });
      },
    },
  );

  const floorPlans = useMemo(() => data ?? [], [data]);
  return {
    floorPlans,
    isLoading,
    isSuccess,
    refetch,
    count: floorPlans.length,
  };
};

const useParseResults = ({
  floorPlans,
  selectedProperty,
  isPropertySection236,
  showRDBasicRent,
  showRDNoteRent,
}) => {
  return useMemo(() => {
    const results = floorPlans.map((floorPlan) => {
      const { isHUD } = getFloorPlanType(floorPlan);

      const { baseMarketRentAmount, grossRentLimit } = calculateBaseMarketRent({
        floorplan: floorPlan,
        property: selectedProperty,
      });
      const quotingRentAmount = calculateRentWithAmenities(
        floorPlan,
        floorPlan.quotingRentAmount,
      );
      const isCommercial =
        floorPlan.isCommercial || floorPlan.isCommercialLeasingActive;

      const isSection236 =
        (floorPlan?.floorPlanAffordablePrograms ?? [])
          .map((fpap) => fpap?.hudCode)
          .includes(HUD_SUBSIDY_CODES.SECTION_236) || isPropertySection236;

      const { hud236MarketRent, hud236BasicRent } = isSection236
        ? parse236Rents({
            floorPlan,
          })
        : { hud236MarketRent: null, hud236BasicRent: null };
      return {
        floorPlanId: floorPlan.id,
        floorPlanMarketingName: floorPlan.marketingName,
        floorPlanName: floorPlan.internalName,
        floorPlanPrograms: getFloorPlanProgramNames(floorPlan),
        bedsBaths: formatFloorPlanBedsBaths(floorPlan),
        sqFt: floorPlan.grossSquareFeet,
        rentableSqFt: floorPlan.rentableSquareFeet,
        numUnits: floorPlan.floorplanUnitCount?.numTotalUnits || 0,
        baseFloorPlanMarketRent: baseMarketRentAmount || 0,
        financialMarketRent: floorPlan.financialMarketRentAmount || 0,
        quotingRent: quotingRentAmount || 0,
        grossRentLimit: grossRentLimit || 0,
        hudGrossRentLimit: isHUD ? floorPlan.HUDGrossRent : 0 || 0,
        suggestedDeposit: floorPlan.estimatedDepositAmount,
        isCommercial,
        isSection236,
        hud236BasicRent: hud236BasicRent,
        hud236MarketRent: hud236MarketRent,
        noteRent: showRDNoteRent ? floorPlan.noteRent : null,
        basicRent: showRDBasicRent ? floorPlan.basicRent : null,
      };
    });
    return { results };
  }, [
    floorPlans,
    isPropertySection236,
    selectedProperty,
    showRDBasicRent,
    showRDNoteRent,
  ]);
};

const useHeaders = ({
  isAffordableMixedProperty,
  hasHUD,
  hasLIHTC,
  filterOptions,
  showSection236,
  showRDBasicRent,
  showRDNoteRent,
}) => {
  return useMemo(() => {
    const baseHeaders = HEADERS({
      isAffordableMixedProperty,
      hasHUD,
      hasLIHTC,
      showSection236,
      showRDBasicRent,
      showRDNoteRent,
    });
    let headers = [];
    baseHeaders.forEach((header) => {
      if (filterOptions[`${header.id}Options`])
        header.filterOptions = filterOptions[`${header.id}Options`];
      headers.push(header);
    });
    return headers;
  }, [
    showSection236,
    showRDBasicRent,
    showRDNoteRent,
    isAffordableMixedProperty,
    hasHUD,
    hasLIHTC,
    filterOptions,
  ]);
};
const useGetFooterRow = ({ sortedAndFilteredResults, filteredHeaders }) => {
  return useMemo(() => {
    const totalsColumns = JSON.parse(JSON.stringify(TOTAL_COLUMNS));
    sortedAndFilteredResults.forEach((result) => {
      Object.keys(totalsColumns).forEach((id) => {
        if (result[id]) {
          const addition =
            typeof result[id] === 'string'
              ? parseFloat(result[id])
              : result[id];
          if (id === 'numUnits') {
            totalsColumns[id].value += addition;
          } else {
            // Add the amount for each unit
            totalsColumns[id].value += addition * (result.numUnits || 0);
          }
        }
      });
    });

    const results = filteredHeaders.reduce((object, { id }) => {
      object[id] = totalsColumns[id] ?? '';
      return object;
    }, {});

    return results;
  }, [sortedAndFilteredResults, filteredHeaders]);
};
const useRows = ({
  intl,
  floorPlans = [],
  isAffordable,
  isMixed,
  openUnitsByFloorplanModal,
  showSection236,
  showRDBasicRent,
  showRDNoteRent,
}) => {
  return useMemo(() => {
    return floorPlans?.map(
      ({
        floorPlanId,
        floorPlanName,
        floorPlanMarketingName,
        floorPlanPrograms,
        bedsBaths,
        sqFt,
        rentableSqFt,
        numUnits,
        baseFloorPlanMarketRent,
        financialMarketRent,
        quotingRent,
        grossRentLimit,
        hudGrossRentLimit,
        suggestedDeposit,
        hud236BasicRent,
        hud236MarketRent,
        noteRent,
        basicRent,
        isCommercial,
        ...rest
      }) => {
        return {
          id: floorPlanId,
          floorPlanName: {
            variant: 'link',
            to: getUrlWithSelectedPropertyId(`/floorPlan/${floorPlanId}`),
            value: floorPlanName,
          },
          floorPlanPrograms: {
            value: getFloorPlanProgramsCellValue(floorPlanPrograms),
          },
          floorPlanMarketingName: {
            value: floorPlanMarketingName,
          },
          bedsBaths: {
            value: bedsBaths,
          },
          sqFt: {
            variant: 'number',
            value: sqFt,
          },
          rentableSqFt: {
            variant: 'number',
            value: rentableSqFt,
          },
          numUnits: {
            variant: 'button',
            value: numUnits || '0',
            onClick:
              numUnits > 0
                ? () =>
                    openUnitsByFloorplanModal({
                      floorPlanId,
                      floorInternalName: floorPlanName,
                      numUnits,
                    })
                : undefined,
          },
          ...(showRDNoteRent && {
            noteRent: {
              variant: 'currency',
              value: noteRent,
            },
          }),
          ...(showRDBasicRent && {
            basicRent: {
              variant: 'currency',
              value: basicRent,
            },
          }),
          baseFloorPlanMarketRent: {
            variant: 'currency',
            value: baseFloorPlanMarketRent,
          },
          financialMarketRent: {
            variant: 'currency',
            value: financialMarketRent,
          },
          quotingRent: {
            variant: 'currency',
            value: quotingRent,
          },
          ...(showSection236
            ? {
                hud236BasicRent: {
                  variant: 'currency',
                  value: hud236BasicRent,
                },
                hud236MarketRent: {
                  variant: 'currency',
                  value: hud236MarketRent,
                },
              }
            : {}),
          grossRentLimit: {
            variant: 'currency',
            value: grossRentLimit,
          },
          ...((isAffordable || isMixed) && {
            hudGrossRentLimit: {
              variant: 'currency',
              value: hudGrossRentLimit,
            },
          }),
          suggestedDeposit: {
            variant: 'currency',
            value: suggestedDeposit,
          },
        };
      },
    );
  }, [
    intl,
    isAffordable,
    isMixed,
    floorPlans,
    openUnitsByFloorplanModal,
    showSection236,
    showRDBasicRent,
    showRDNoteRent,
  ]);
};

const useGetFilterOptions = ({ results }) => {
  return useMemo(() => {
    const options = {
      bedsBathsOptions: [],
      floorPlanNameOptions: [],
    };

    if (results) {
      results.forEach(({ floorPlanName, bedsBaths }) => {
        if (floorPlanName?.length)
          options.floorPlanNameOptions.push(floorPlanName);
        if (bedsBaths?.length) options.bedsBathsOptions.push(bedsBaths);
      });

      options.floorPlanNameOptions = Array.from(
        new Set(options.floorPlanNameOptions),
      )
        .sort()
        .map((floorPlan) => ({
          text: floorPlan,
          value: floorPlan?.toLowerCase(),
        }));

      options.bedsBathsOptions = Array.from(new Set(options.bedsBathsOptions))
        .sort()
        .map((bedsBaths) => ({
          text: bedsBaths,
          value: bedsBaths,
        }));
    }

    return options;
  }, [results]);
};

const useCSVPDFExport = ({
  filteredHeaders,
  rows,
  propertyName,
  hasAnyFilters,
}) => {
  const onCSVButtonClick = useCallback(() => {
    const fileName = `FloorPlan_${propertyName
      ?.split(' ')
      .join('-')}_${formatDateDB(new Date())}`;

    exportAndDownloadCSV({
      filteredHeaders,
      fileName,
      rows,
      hasAnyFilters,
    });
  }, [filteredHeaders, rows, hasAnyFilters, propertyName]);

  return { onCSVButtonClick };
};
